import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type MessageDto } from '../api/client.ts';
import { searchable } from './search.ts';

/**
 * How long the field waits before asking. The design says the list updates on
 * every character, and at this delay it does — what it stops is a request per
 * keystroke, where four of the five would be answered about a word nobody had
 * finished typing.
 */
const SETTLE_MS = 200;

/** Matches loaded at a time. Well over a screenful, so the arrows rarely wait. */
const PAGE = 40;

export interface MessageSearch {
  query: string;
  setQuery: (query: string) => void;
  /** Newest match first, which is the order the screen lists them in. */
  results: MessageDto[];
  /** Matches in the whole conversation, loaded or not — the «из 4». */
  total: number;
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  /** Empties the field and everything found for it. */
  reset: () => void;
}

/**
 * Where a search is looking: one conversation by id, `'all'` for every
 * conversation this person is in — which is what the field above the chat list
 * asks — or `null` for a field that is not on screen.
 */
export type SearchScope = string | 'all' | null;

/**
 * The state behind «Поиск по сообщениям»: what has been typed, what came back,
 * and one page more of it on request.
 *
 * A counter rather than an AbortController guards against the answers arriving
 * out of order — typing «дог» then «догов» is two requests in flight, and the
 * one that matters is the one asked for last. Aborting would do the same for
 * the network but not for a reply already on its way back.
 */
export function useMessageSearch(scope: SearchScope, initialQuery = ''): MessageSearch {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<MessageDto[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const asked = useRef(0);
  const morePending = useRef(false);

  const reset = useCallback(() => {
    asked.current += 1;
    setQuery(initialQuery);
    setResults([]);
    setTotal(0);
    setHasMore(false);
    setLoading(false);
  }, [initialQuery]);

  // A scope of its own results: switching chats with the field still holding a
  // word would otherwise show the last chat's matches under it.
  useEffect(() => reset(), [scope, reset]);

  useEffect(() => {
    const mine = (asked.current += 1);
    if (scope === null || !searchable(query)) {
      setResults([]);
      setTotal(0);
      setHasMore(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const page =
            scope === 'all'
              ? await api.searchAllMessages(query, { limit: PAGE })
              : await api.searchMessages(scope, query, { limit: PAGE });
          if (asked.current !== mine) return;
          setResults(page.messages);
          setTotal(page.total);
          setHasMore(page.hasMore);
        } catch {
          // Offline, most likely. An empty list under the word that was typed
          // is the same thing the server says when it finds nothing, and the
          // next keystroke asks again.
          if (asked.current !== mine) return;
          setResults([]);
          setTotal(0);
          setHasMore(false);
        } finally {
          if (asked.current === mine) setLoading(false);
        }
      })();
    }, SETTLE_MS);

    return () => window.clearTimeout(timer);
  }, [scope, query]);

  const loadMore = useCallback(() => {
    const last = results[results.length - 1];
    if (scope === null || !hasMore || !last || morePending.current) return;

    const mine = asked.current;
    morePending.current = true;
    void (async () => {
      try {
        const page =
          scope === 'all'
            ? await api.searchAllMessages(query, { before: last.id, limit: PAGE })
            : await api.searchMessages(scope, query, { before: last.id, limit: PAGE });
        // The query moved on while this was in flight; its answer is not about
        // what is on screen any more.
        if (asked.current !== mine) return;
        setResults((current) => {
          const known = new Set(current.map((message) => message.id));
          return [...current, ...page.messages.filter((message) => !known.has(message.id))];
        });
        setHasMore(page.hasMore);
      } catch {
        // hasMore is left standing, so the next scroll asks again.
      } finally {
        morePending.current = false;
      }
    })();
  }, [scope, query, hasMore, results]);

  return { query, setQuery, results, total, loading, hasMore, loadMore, reset };
}
